import type { AdminIdentity, ApiEnvelope, Language, ManualChapter, ManualPage, SectionType } from './types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'stla_manual_access_token';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly details?: unknown) {
    super(message);
  }
}

function token(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

async function request<T>(path: string, init: RequestInit = {}, authenticated = false): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (authenticated && token()) headers.set('Authorization', `Bearer ${token()}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const payload = await response.json().catch(() => ({})) as Partial<ApiEnvelope<T>> & Record<string, unknown>;
  if (!response.ok || payload.ok === false) {
    const message = typeof payload.message === 'string' ? payload.message : `Error ${response.status}`;
    throw new ApiError(response.status, message, payload.errors ?? payload);
  }
  return (payload.data ?? payload) as T;
}

function unwrapLogin(payload: unknown): Record<string, unknown> {
  let current = payload as Record<string, unknown>;
  if (current && typeof current === 'object' && current.data && typeof current.data === 'object') current = current.data as Record<string, unknown>;
  return current;
}

export const api = {
  hasSession: () => Boolean(token()),
  clearSession: () => sessionStorage.removeItem(TOKEN_KEY),

  async login(email: string, password: string, language: Language): Promise<void> {
    const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Locale': language };
    if (import.meta.env.VITE_LOGIN_DOMAIN) headers['X-Domain'] = import.meta.env.VITE_LOGIN_DOMAIN;
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST', headers,
      body: JSON.stringify({ email, password, validar: true, Device: 'web', locale: language, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' }),
    });
    const raw = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(response.status, (raw as { message?: string }).message || 'No fue posible iniciar sesión.', raw);
    const outer = unwrapLogin(raw);
    const status = outer.status as string | undefined;
    if (status && status !== 'OK') throw new ApiError(409, (outer.message as string) || `El acceso requiere completar: ${status}`, outer);
    const data = unwrapLogin(outer);
    const accessToken = data.token;
    if (typeof accessToken !== 'string' || !accessToken) throw new ApiError(500, 'La API no devolvió el token de acceso esperado.', raw);
    sessionStorage.setItem(TOKEN_KEY, accessToken);
  },

  publicTree: (language: Language) => request<ManualChapter[]>(`/manual/publico/arbol?idioma=${language}`),
  publicPage: (slug: string, language: Language) => request<ManualPage>(`/manual/publico/paginas/${encodeURIComponent(slug)}?idioma=${language}`),
  adminMe: () => request<AdminIdentity>('/manual/admin/me', {}, true),
  adminTree: (language: Language) => request<ManualChapter[]>(`/manual/admin/arbol?idioma=${language}`, {}, true),
  adminPage: (slug: string, language: Language) => request<ManualPage>(`/manual/admin/paginas/${encodeURIComponent(slug)}?idioma=${language}`, {}, true),

  saveChapter: (input: { id?: number; codigo: string; orden: number; idioma: Language; titulo: string; descripcion: string }) =>
    request<{ id: number }>(`/manual/admin/capitulos${input.id ? `/${input.id}` : ''}`, {
      method: input.id ? 'PUT' : 'POST',
      body: JSON.stringify({ codigo: input.codigo, orden: input.orden, traducciones: [{ idioma: input.idioma, titulo: input.titulo, descripcion: input.descripcion || null }] }),
    }, true),

  savePage: (input: { id?: number; capitulo_id: number; slug: string; orden: number; idioma: Language; titulo: string; resumen: string }) =>
    request<{ id: number }>(`/manual/admin/paginas${input.id ? `/${input.id}` : ''}`, {
      method: input.id ? 'PUT' : 'POST',
      body: JSON.stringify({ capitulo_id: input.capitulo_id, slug: input.slug, orden: input.orden, traducciones: [{ idioma: input.idioma, titulo: input.titulo, resumen: input.resumen || null }] }),
    }, true),

  saveSection: (input: { id?: number; pagina_id: number; tipo: SectionType; orden: number; idioma: Language; titulo: string; contenido: string }) =>
    request<{ id: number }>(`/manual/admin/secciones${input.id ? `/${input.id}` : ''}`, {
      method: input.id ? 'PUT' : 'POST',
      body: JSON.stringify({ pagina_id: input.pagina_id, tipo: input.tipo, orden: input.orden, traducciones: [{ idioma: input.idioma, titulo: input.titulo || null, contenido: input.contenido || null }] }),
    }, true),

  async uploadImage(sectionId: number, file: File, language: Language, alt: string, caption: string, order: number): Promise<{ id: number }> {
    const form = new FormData();
    form.append('imagen', file);
    form.append('orden', String(order));
    form.append('traducciones[0][idioma]', language);
    form.append('traducciones[0][texto_alternativo]', alt);
    form.append('traducciones[0][pie]', caption);
    return request<{ id: number }>(`/manual/admin/secciones/${sectionId}/imagenes`, { method: 'POST', body: form }, true);
  },

  publish: (pageId: number, comment: string) => request<{ pagina_id: number; version: number; estado: string }>(`/manual/admin/paginas/${pageId}/publicar`, { method: 'POST', body: JSON.stringify({ comentario: comment || null }) }, true),
};
